import { LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import emitter, { EVENTS } from "./emitter";
import { ProduceStream, ProduceUser } from "../types";

/**
 * Best display name for a livechat participant. The livechat-session
 * `screenName` is empty for non-owner guests, so fall back to the linked
 * messenger account's name (`user.screenName`) before giving up. Used when
 * capturing recording participants and drawing recorded video tiles so guests
 * don't surface as "User <id>".
 */
export function liveChatUserName(u: LiveChatUser): string {
  return u.screenName?.trim() || u.user?.screenName?.trim() || "";
}

/**
 * Stable id for a recording participant. Prefer the messenger ACCOUNT id
 * (`LiveChatUser.userId`) so the backend can resolve the current name later and
 * reflect renames; fall back to the ephemeral livechat SESSION id (`.id`) for
 * guests who have no account (userId 0/empty). MUST be used identically
 * wherever a recording derives a per-participant id — the backend joins audio
 * tracks to participant names by exact id match, so a mismatch drops the name.
 * (Direct calls already pass account ids; this matters for group conferences,
 * where `.id` is the session id and only `.userId` resolves to an account.)
 */
export function recordingParticipantId(u: LiveChatUser): string {
  const accountId = u?.userId != null ? String(u.userId).trim() : "";
  const id = accountId && accountId !== "0" ? accountId : u?.id != null ? String(u.id).trim() : "";
  // Strip to the backend's join charset (same as the recorder/uploader's
  // sanitizeUserId) so the participants-metadata key and the audio-track key
  // match byte-for-byte — the backend joins speaker names to audio tracks by
  // exact id equality, and a divergent key would silently drop the name.
  return id.replace(/[^a-zA-Z0-9_-]/g, "");
}

export function getProduceStreams(user: ProduceUser): ProduceStream[] {
  const result: ProduceStream[] = [];
  result.push({
    id: user.user.id,
    user: user.user,
    stream: user.stream,
    audioStream: user.audioStream ?? null,
    // Presence (constrains.audioEnabled, from producers-updated) AND not muted
    // (audioMuted, from the AUDIO_MUTED data event). With cheap-mute the audio
    // producer stays open, so presence alone can't detect mute.
    hasAudio: (user.constrains?.audioEnabled ?? false) && !user.audioMuted,
    hasVideo: user.constrains?.videoEnabled ?? false,
    isScreen: false,
    // Only the camera entry carries isSpeaking — the screen-share entry is
    // a visual surface only, never the "speaker".
    isSpeaking: user.isSpeaking,
  });
  if (user.constrains?.shareEnabled && user.screenStream) {
    result.push({
      id: user.user.id + "__screen",
      user: user.user,
      stream: user.screenStream,
      hasAudio: true,
      hasVideo: true,
      isScreen: true,
    });
  }

  return result;
}

export function checkNeedMirrorReflection(stream: ProduceStream, _me: ProduceUser | null): boolean {
  // Mirror all cameras (consistent look), but not screen shares
  return !stream.isScreen;
}

export function updateVideoStream(video: HTMLVideoElement | null, stream: MediaStream) {
  if (!video) return;
  video.pause();
  video.srcObject = stream;
  video.oncanplay = () => {
    video.play().catch((e) => {
      if (!video) throw e;
      setTimeout(
        () =>
          video?.play().catch((e) => {
            // eslint-disable-next-line no-console
            console.error(e);
            emitter.emit(EVENTS.USER_DID_NOT_INTERACT_DOC_FIRST, () => video?.play());
          }),
        500
      );
    });
  };
}
